import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import scrollIntoView from 'dom-scroll-into-view';
import UserAvatar from 'components/pure/utils/UserAvatar';
import User from 'records/User';
import {List} from 'immutable';
import partitionBy from 'utils/partitionBy';
import moment from 'moment';
import MiniAvatarList from 'components/dashboard/admin/MiniAvatarList';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import {Link} from 'react-router';
import './AdminDashCoachingPlanRow.styl';
import buildUrl from 'components/dashboard/buildUrl';
import ReactDOM from 'react-dom';
import ReviewTooltip from './ReviewTooltip';
import {TOOLTIP_DELAY} from 'components/StatsPage2.utils';
import {getFromPathState, ReviewLink} from 'components/dashboard/admin/ReviewLink';

export default class AdminDashCoachingPlanRow extends React.PureComponent {
  render() {
    const {
      user,
      plans = List(),
      overdue = List(),
      reviews = List(),
      adminId,
      fromPath,
      allPlans,
      reviewTypes,
      selected,
    } = this.props;

    const [completed, incomplete] = partitionBy(plans, p => p.completed);
    const active = incomplete.filter(p => !overdue.some(od => od.id === p.id));

    // group reviews by coach - create a map of userid -> [reviewDate, reviewTooltip]
    // that we'll later sort and render
    const tooltipsByUser = reviews
      .groupBy(r => r.coachUserId)
      .mapEntries(([k, reviews]) => [
        k,
        reviews.map(review => [
          review,
          allPlans.find(p => p.id === review.planId),
          <ReviewTooltip
            adminId={adminId}
            userId={user.id}
            review={review}
            plan={allPlans.find(p => p.id === review.planId)}
            fromPath={fromPath}
            reviewTypes={reviewTypes}
          />,
        ]),
      ]);

    const isCoachOnly = user.is_coach && !user.isManager();
    let managerId = null;
    let coachId = null;
    let page = '';
    if (isCoachOnly) {
      coachId = user.id;
      page = 'coaching-plans';
    } else {
      managerId = user.id;
      page = 'your-coaching';
    }
    const pathname = buildUrl({adminId, managerId, coachId}) + `/${page}`;
    const state = getFromPathState(fromPath);

    if (selected) {
      window.requestAnimationFrame(() => {
        const domNode = ReactDOM.findDOMNode(this);
        const rect = domNode.getBoundingClientRect();
        const shouldScroll = rect.top < 0 || rect.bottom > window.innerHeight;
        if (shouldScroll) {
          scrollIntoView(domNode, window, {
            alignWithTop: false,
            offsetBottom: 20,
          });
        }
      });
    }

    return (
      <tr className={classNames('admin-dash-coaching-plan-row', selected && 'selected')}>
        <td>
          <div className="coach-cell">
            <UserAvatar user={user} />
            <Link to={{pathname, state}}>
              <span className="generic-link">{user.getFullName()}</span>
            </Link>
          </div>
        </td>
        <td>
          <MiniAvatarList
            plans={active}
            adminId={adminId}
            managerId={managerId}
            fromPath={fromPath}
          />
        </td>
        <td>
          <MiniAvatarList
            plans={completed}
            adminId={adminId}
            managerId={managerId}
            fromPath={fromPath}
          />
        </td>
        <td>
          <MiniAvatarList
            overdue
            plans={overdue}
            adminId={adminId}
            managerId={managerId}
            fromPath={fromPath}
          />
        </td>
        <td>
          <div className="review-cell">
            {tooltipsByUser
              .get(user.id, List())
              .sort(([l], [r]) => l.completedAt - r.completedAt)
              .map(([review, plan, tooltip], i) => {
                const reviewType = reviewTypes.get(review.reviewTypeId);
                if (!reviewType) {
                  console.log(`missing review type ${review.reviewTypeId} for review ${review.id}`);
                }

                return (
                  <GenericTooltip
                    hoverBehavior
                    positionTop
                    key={`tooltip-${user.id}-${i}`}
                    tooltip={tooltip}
                    className="admin-dash-tooltip-wrapper"
                    delay={TOOLTIP_DELAY}>
                    <div>
                      <ReviewLink
                        adminId={adminId}
                        managerId={managerId}
                        review={review}
                        plan={plan}
                        fromPath={fromPath}>
                        <ReviewBlock reviewType={reviewType} />
                      </ReviewLink>
                    </div>
                  </GenericTooltip>
                );
              })}
          </div>
        </td>
      </tr>
    );
  }
}

const ReviewBlock = ({reviewType}) => {
  const defaultColor = '#CED5DE';
  const backgroundColor = reviewType?.meetingCategory?.color || defaultColor;

  return (
    <div
      className="review-block"
      style={{
        backgroundColor,
      }}
    />
  );
};

AdminDashCoachingPlanRow.propTypes = {
  startDate: PropTypes.instanceOf(moment),
  endDate: PropTypes.instanceOf(moment),
  index: PropTypes.number,
  user: PropTypes.instanceOf(User),
  plans: PropTypes.instanceOf(List), // List<CoachingPlan>
};
